import React, { useState } from "react";
import { AuditItem } from "../seoAnalyzer";
import { CheckCircle2, AlertTriangle, XCircle, Code, Copy, Check, ChevronDown, ChevronUp } from "lucide-react";

interface AuditFeedProps {
  audits: AuditItem[];
}

export const AuditFeed: React.FC<AuditFeedProps> = ({ audits }) => {
  const [statusFilter, setStatusFilter] = useState<"all" | "error" | "warning" | "pass">("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const passCount = audits.filter((a) => a.status === "pass").length;
  const warningCount = audits.filter((a) => a.status === "warning").length;
  const errorCount = audits.filter((a) => a.status === "error").length;

  const filteredAudits = audits.filter((audit) => {
    if (statusFilter !== "all" && audit.status !== statusFilter) return false;
    if (categoryFilter !== "all" && audit.category !== categoryFilter) return false;
    return true;
  });

  const handleCopy = (id: string, text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const getStatusIcon = (status: AuditItem["status"]) => {
    if (status === "pass") return <CheckCircle2 size={20} color="var(--neon-emerald)" />;
    if (status === "warning") return <AlertTriangle size={20} color="var(--neon-amber)" />;
    return <XCircle size={20} color="var(--neon-crimson)" />;
  };

  return (
    <div>
      {/* Filter Controls */}
      <div className="audit-filter-bar">
        <div className="filter-group">
          <button
            className={`filter-chip ${statusFilter === "all" ? "active" : ""}`}
            onClick={() => setStatusFilter("all")}
          >
            All Audits ({audits.length})
          </button>
          <button
            className={`filter-chip ${statusFilter === "error" ? "active" : ""}`}
            style={statusFilter === "error" ? { borderColor: "var(--neon-crimson)", color: "var(--neon-crimson)" } : {}}
            onClick={() => setStatusFilter("error")}
          >
            Errors ({errorCount})
          </button>
          <button
            className={`filter-chip ${statusFilter === "warning" ? "active" : ""}`}
            style={statusFilter === "warning" ? { borderColor: "var(--neon-amber)", color: "var(--neon-amber)" } : {}}
            onClick={() => setStatusFilter("warning")}
          >
            Warnings ({warningCount})
          </button>
          <button
            className={`filter-chip ${statusFilter === "pass" ? "active" : ""}`}
            style={statusFilter === "pass" ? { borderColor: "var(--neon-emerald)", color: "var(--neon-emerald)" } : {}}
            onClick={() => setStatusFilter("pass")}
          >
            Passed ({passCount})
          </button>
        </div>

        <div className="filter-group">
          <span style={{ fontSize: "0.8rem", color: "var(--text-secondary)", alignSelf: "center" }}>CATEGORY:</span>
          {["all", "seo", "performance", "accessibility", "security", "social"].map((cat) => (
            <button
              key={cat}
              className={`filter-chip ${categoryFilter === cat ? "active" : ""}`}
              onClick={() => setCategoryFilter(cat)}
            >
              {cat.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Audits List */}
      <div className="audits-list">
        {filteredAudits.length === 0 ? (
          <div className="cyber-card" style={{ textCenter: "center", padding: "2rem" }}>
            <p style={{ color: "var(--text-secondary)" }}>No audit diagnostics matching selected filter criteria.</p>
          </div>
        ) : (
          filteredAudits.map((audit) => (
            <div key={audit.id} className={`cyber-card audit-card audit-card-${audit.status}`}>
              <div className="audit-header">
                <div className="audit-title-area">
                  <div className="audit-status-icon">{getStatusIcon(audit.status)}</div>
                  <div>
                    <h3 className="audit-item-title">{audit.title}</h3>
                    <p className="audit-desc">{audit.description}</p>

                    {audit.displayValue && (
                      <div
                        style={{
                          marginTop: "0.4rem",
                          fontFamily: "var(--font-mono)",
                          fontSize: "0.8rem",
                          color: "var(--neon-cyan)",
                        }}
                      >
                        Value: {audit.displayValue}
                      </div>
                    )}
                  </div>
                </div>

                <div className="audit-badges">
                  <span className={`impact-tag impact-${audit.impact}`}>{audit.impact} IMPACT</span>
                </div>
              </div>

              {/* Recommendation & Code snippet trigger */}
              <div style={{ marginTop: "0.85rem", borderTop: "1px solid rgba(255,255,255,0.05)", paddingTop: "0.75rem" }}>
                <div style={{ fontSize: "0.85rem", color: "var(--text-primary)", fontWeight: 500 }}>
                  <span style={{ color: "var(--neon-cyan)", fontWeight: 700 }}>AI RECOMMENDATION:</span>{" "}
                  {audit.recommendation}
                </div>

                {audit.codeSnippet && (
                  <div>
                    <button
                      className="audit-action-btn"
                      onClick={() => setExpandedId(expandedId === audit.id ? null : audit.id)}
                    >
                      <Code size={14} />
                      <span>{expandedId === audit.id ? "HIDE CODE SNIPPET" : "VIEW RECOMMENDED CODE FIX"}</span>
                      {expandedId === audit.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </button>

                    {expandedId === audit.id && (
                      <div className="code-box">
                        <button className="copy-btn" onClick={() => handleCopy(audit.id, audit.codeSnippet!)}>
                          {copiedId === audit.id ? <Check size={12} color="var(--neon-emerald)" /> : <Copy size={12} />}
                          <span>{copiedId === audit.id ? "COPIED!" : "COPY FIX"}</span>
                        </button>
                        <pre>
                          <code>{audit.codeSnippet}</code>
                        </pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
